import { generatePath } from 'react-router-dom';

import { OwnerParams } from 'shared/types';
import { ROOT_PATH, VISITS_SUB_PATH } from 'shared/consts';
import { VisitUiType } from 'store/visits/types';
import { Step } from 'components/visit-form/types';

/**
 * Builds the path to the visits list page
 * @param ownerParams - Owner parameters (ownerType and ownerId)
 * @returns Full path: '/visitor-registration/building/{ownerId}/visits'
 */
export const buildVisitsListPath = (ownerParams: OwnerParams): string => {
  return generatePath(`${ROOT_PATH}/${VISITS_SUB_PATH}`, ownerParams);
};

/**
 * Builds the path to a specific visit details page
 * @param ownerParams - Owner parameters (ownerType and ownerId)
 * @param visitUiType - Visit list type (upcoming, past, or all)
 * @param visitId - Visit identifier
 * @returns Full path: '/visitor-registration/building/{ownerId}/visits/{visitUiType}/{visitId}'
 */
export const buildVisitDetailsPath = (ownerParams: OwnerParams, visitUiType: VisitUiType, visitId: string): string => {
  // Generate path manually since generatePath doesn't support dynamic segments beyond the path pattern
  return `${generatePath(`${ROOT_PATH}/${VISITS_SUB_PATH}`, ownerParams)}/${visitUiType}/${visitId}`;
};

/**
 * Builds the path to the new visit page
 * @param ownerParams - Owner parameters (ownerType and ownerId)
 * @param step - Optional visit form step (defaults to 'visitor')
 * @returns Full path: '/visitor-registration/building/{ownerId}/visits/new' or '/visitor-registration/building/{ownerId}/visits/new/{step}'
 */
export const buildNewVisitPath = (ownerParams: OwnerParams, step?: Step): string => {
  const basePath = `${generatePath(`${ROOT_PATH}/${VISITS_SUB_PATH}`, ownerParams)}/new`;
  if (step) {
    return `${basePath}/${step}`;
  }
  return basePath;
};

/**
 * Builds the path to edit a specific visit
 * @param ownerParams - Owner parameters (ownerType and ownerId)
 * @param visitId - Visit identifier
 * @param step - Optional visit form step (e.g., 'visitor' or 'date')
 * @returns Full path: '/visitor-registration/building/{ownerId}/visits/{visitUiType}/{visitId}/edit' or with step
 */
export const buildEditVisitPath = (ownerParams: OwnerParams, visitId: string, step?: Step): string => {
  const basePath = `${generatePath(`${ROOT_PATH}/${VISITS_SUB_PATH}`, ownerParams)}/edit/${visitId}`;
  if (step) {
    return `${basePath}/${step}`;
  }
  return basePath;
};

/**
 * Builds the path to cancel/delete a specific visit
 * @param ownerParams - Owner parameters (ownerType and ownerId)
 * @param visitId - Visit identifier
 * @returns Full path: '/visitor-registration/building/{ownerId}/visits/view/{visitId}/delete'
 */
export const buildCancelVisitPath = (ownerParams: OwnerParams, visitId: string): string => {
  return `${generatePath(`${ROOT_PATH}/${VISITS_SUB_PATH}`, ownerParams)}/view/${visitId}/delete`;
};

/**
 * Builds the root path with owner parameters
 * @param ownerParams - Owner parameters (ownerType and ownerId)
 * @returns Full path: '/visitor-registration/{ownerType}/{ownerId}'
 */
export const buildRootPath = (ownerParams: OwnerParams): string => {
  return generatePath(ROOT_PATH, ownerParams);
};
